import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { format } from 'date-fns';
import React from 'react';
import { eocTestId } from './test-ids';

type Props = {
  close: () => void;
  onConfirm: () => void;
  onUpdate: () => void;
  payerType: string | null;
  payerName: string | null;
  payerStartDate?: string | null;
  payerEndDate?: string | null;
  hasNoPayer?: boolean;
};

const formatPayerDate = (dateStr?: string | null) => {
  if (!dateStr) return null;
  try {
    const date = new Date(
      new Date(dateStr).valueOf() + new Date(dateStr).getTimezoneOffset() * 60 * 1000
    );
    return format(date, 'MMM dd, yyyy');
  } catch {
    return dateStr;
  }
};

const formatPayerDateMDY = (dateStr?: string | null) => {
  if (!dateStr) return '';
  try {
    const date = new Date(
      new Date(dateStr).valueOf() + new Date(dateStr).getTimezoneOffset() * 60 * 1000
    );
    return format(date, 'MM-dd-yyyy');
  } catch {
    return dateStr;
  }
};

const PayerConfirmationModal = (props: Props) => {
  const {
    onConfirm,
    onUpdate,
    payerType,
    payerName,
    payerStartDate,
    payerEndDate,
    hasNoPayer = false,
  } = props;

  return (
    <Box>
      <DialogTitle
        sx={{
          m: 0,
          p: '24px 24px 0px 24px',
          pb: '16px',
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 1.5,
            letterSpacing: 0.0075,
            color: '#111927',
          }}
        >
          {hasNoPayer ? 'No Payer Assigned for Episode Start Date' : 'Correct Payer?'}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          p: '0px 24px 24px 24px',
          '&.MuiDialogContent-root': {
            paddingTop: '8px',
          },
        }}
      >
        <Typography
          sx={{
            color: '#6C737F',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.5,
          }}
          variant="body2"
        >
          {hasNoPayer ? (
            <>
              We couldn&apos;t find an active payer on the selected start date of this Episode of
              Care.
              <br />
              To ensure accurate documentation and billing, please review the payer information
              before continuing.
            </>
          ) : (
            <>
              The current payer for this patient is
              <Box
                component="span"
                sx={{ fontWeight: 600, color: '#111927' }}
              >
                {' '}
                {payerType} - {payerName}
              </Box>
              . Is this the updated payer?
            </>
          )}
        </Typography>
        {!hasNoPayer && (payerStartDate || payerEndDate) && (
          <>
            <Stack
              direction="row"
              spacing={3}
              sx={{ mt: 2 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: '#6C737F' }}
                >
                  Start Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#111927' }}
                >
                  {formatPayerDate(payerStartDate) || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: '#6C737F' }}
                >
                  End Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#111927' }}
                >
                  {formatPayerDate(payerEndDate) || 'Open'}
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mt: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 171, 0, 0.08)',
              }}
            >
              <WarningAmberIcon sx={{ color: '#B76E00', fontSize: '20px', mt: '2px' }} />
              <Typography
                variant="body2"
                sx={{ color: '#B76E00' }}
              >
                Payer [{payerType}] - {payerName} already covers an existing EOC (Start Date:{' '}
                {formatPayerDateMDY(payerStartDate)}) Adding a new EOC will combine all EOC data in
                your Section O and LOS reports.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: '16px 24px 24px 24px',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        <Button
          variant="text"
          onClick={onUpdate}
          data-testid={eocTestId('payer-update')}
        >
          {hasNoPayer ? 'Update Payers' : 'Update Payer'}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          type="submit"
          data-testid={eocTestId('payer-confirm')}
        >
          {hasNoPayer ? 'Continue Without Payer' : 'Confirm Payer'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default PayerConfirmationModal;
